import {
  InstallationAdministrationV1,
  InstallationV1,
  type OperatorEnvironment,
} from "@strust/contracts";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api/api-provider.tsx";
import { ApiRequestError } from "./api/client.ts";
import { defineEndpoint } from "./api/endpoint.ts";

// The installation this Explorer belongs to (ADR 0011, design D7 of
// bind-operator-environment-to-installation): one installation serves exactly one operator
// environment, read from the server (`GET /explorer/v1/installation`, public, no session). The hub
// never derives it from the hostname, never lets the user change it and never defaults to one.

export const installationEndpoint = defineEndpoint({
  method: "GET",
  path: "/installation",
  response: InstallationV1,
});

export const INSTALLATION_QUERY_KEY = ["installation"] as const;

/**
 * The installation's environment and API base URL, fetched once (the configuration of a running
 * installation never changes: `staleTime: Infinity`). Loading and error are the caller's to show;
 * nothing falls back to an environment.
 */
export function useInstallation() {
  const client = useApiClient();
  return useQuery({
    queryKey: INSTALLATION_QUERY_KEY,
    queryFn: ({ signal }) => client.call(installationEndpoint, { signal }),
    staleTime: Infinity,
  });
}

export const installationAdministrationEndpoint = defineEndpoint({
  method: "GET",
  path: "/installation/administration",
  response: InstallationAdministrationV1,
});

/**
 * Whether this installation has the Platform area (adopt-installation-per-customer design D14,
 * spec explorer-access "[FR] Console areas of the installation"): read after the login (a live
 * session), once. `customer` is true only when the server answers `disabled`; while loading or on
 * an error it is false, so the "Integration" screen is never offered on a guess (the API refuses
 * it outside a customer installation anyway).
 */
export function useInstallationAdministration(enabled = true) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: [...INSTALLATION_QUERY_KEY, "administration"] as const,
    enabled,
    queryFn: ({ signal }) => client.call(installationAdministrationEndpoint, { signal }),
    staleTime: Infinity,
  });
  return {
    isPending: enabled && query.isPending,
    customer: query.data?.platform_administration === "disabled",
    /** The read failed (not a 404: a server without the route is no customer installation). */
    isError:
      query.isError &&
      !(query.error instanceof ApiRequestError && query.error.code === "not_found"),
    refetch: query.refetch,
  };
}

/** The search parameter the removed environment selector put on every Explorer URL. */
export const LEGACY_ENVIRONMENT_PARAM = "environment";

/**
 * The operator environment an old `?environment=` link named, or `undefined` for a value that
 * names none (the former name of `staging`, design D2, is no value any more: such a link is cleaned
 * without a notice).
 */
export function environmentOfLegacyLink(value: unknown): OperatorEnvironment | undefined {
  if (value === "staging") return "staging";
  if (value === "production") return "production";
  return undefined;
}

/**
 * The other installation's environment an old link was made for, or `undefined` when the link
 * named this installation's environment (or nothing valid): only then is the user told once
 * (spec explorer-access, scenario "Old link with the environment parameter").
 */
export function foreignLinkEnvironment(
  linkValue: unknown,
  installation: OperatorEnvironment,
): OperatorEnvironment | undefined {
  const linked = environmentOfLegacyLink(linkValue);
  return linked !== undefined && linked !== installation ? linked : undefined;
}
